'use client';

import {
  forwardRef,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useLocale } from 'next-intl';
import { FieldError } from 'react-hook-form';
import { cx } from '@/lib/utils';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  leading?: string | React.ReactNode;
  trailing?: string | React.ReactNode;
  maxLength?: number;
  value?: string | number;
  inputClassName?: string;
  messages?: string | string[];
  error?: FieldError | boolean;
  errorType?: string;
  noLocale?: boolean;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

const TextField = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      label,
      value,
      leading,
      onChange,
      messages,
      trailing,
      max,
      maxLength,
      inputClassName,
      noLocale,
      error,
      errorType,
      ...otherProps
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current!);

    const [touched, setTouched] = useState(
      errorType === 'manual' ? true : false,
    );

    const touchedError = !!error && touched;

    const locale = useLocale();

    const containerClasses = `
    relative
    h-14
    w-full
    ${otherProps.className || ''}
  `.trim();

    const inputBaseClasses = `
    absolute inset-0 w-full border rounded-xl outline-none p-4 
    placeholder:text-base placeholder:text-on-surface-low placeholder:font-normal  placeholder-inherit
    ${
      touchedError
        ? 'border-danger focus:ring-danger focus:border-danger'
        : 'border-outline'
    }
    ${Boolean(label) ? 'pt-7' : ''}
    ${trailing ? (locale == 'en' ? 'pr-10' : 'pr-4') : ''}
    ${
      leading
        ? `motion-safe:transition-[padding-left,padding-right] motion-safe:duration-200
           ${
             typeof leading === 'string'
               ? `${
                   noLocale ? 'pl-10' : 'rtl:pr-10 ltr:pl-10'
                 } motion-safe:ease-in-out ${
                   noLocale ? 'pl-10' : 'ltr:focus:pl-10 rtl:focus:pr-10'
                 } [&:not(:placeholder-shown)]:pl-10`
               : ''
           }`
        : ''
    }
    ${noLocale ? 'text-left' : ''}
    bg-white text-on-surface
    body-large peer hover:[&:not(:focus)]:border-outline-variant
    transition-colors duration-200
  `;

    const inputClasses = `${inputBaseClasses}`.trim();

    // Define reusable class name constants
    const peerFocusClasses = `
    peer-focus:top-2 ${
      noLocale
        ? 'left-[13px]'
        : 'rtl:peer-focus:right-[13px] ltr:peer-focus:left-[13px]'
    }
    peer-focus:text-xs peer-focus:font-medium peer-focus:px-1 peer-focus:gap-0.5
  `.trim();

    const peerAutoFillClasses = `
    peer-autofill:top-2 ${
      noLocale
        ? 'left-[13px]'
        : 'rtl:peer-autofill:right-[13px] ltr:peer-autofill:left-[13px]'
    }
    peer-autofill:text-xs peer-autofill:font-medium peer-autofill:px-1 peer-autofill:gap-0.5
  `.trim();

    const peerNotPlaceholderShownClasses = `
    peer-[:not(:placeholder-shown)]:top-2 ${
      noLocale
        ? 'left-[13px]'
        : 'rtl:peer-[:not(:placeholder-shown)]:right-[13px] ltr:peer-[:not(:placeholder-shown)]:left-[13px]'
    }
    peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:font-medium
    peer-[:not(:placeholder-shown)]:px-1 peer-[:not(:placeholder-shown)]:gap-0.5
  `.trim();

    // Update labelClasses
    const labelClasses = `
    absolute ${
      noLocale ? 'left-4' : 'rtl:right-4 ltr:left-4'
    } top-4 flex gap-1 rounded-[0.625rem] text-on-surface-low pointer-events-none bg-[transparent]
    motion-safe:transition-[top,right,left,font-size,font-weight,bg-white] motion-safe:duration-200
    motion-safe:ease-in-out
    ${peerFocusClasses}
    ${peerAutoFillClasses}
    ${peerNotPlaceholderShownClasses}
     ${
       leading
         ? noLocale
           ? 'peer-focus:pl-7 peer-[:not(:placeholder-shown)]:pl-7 peer-autofill:pl-7'
           : 'ltr:peer-focus:pl-7 rtl:peer-focus:pr-7 ltr:peer-[:not(:placeholder-shown)]:pl-7 rtl:peer-[:not(:placeholder-shown)]:pr-7 ltr:peer-autofill:pl-7 rtl:peer-autofill:pr-7'
         : ''
     }
    /* When the input is empty */
    peer-placeholder-shown:top-4 ${
      noLocale
        ? 'left-4'
        : 'ltr:peer-placeholder-shown:right-4 rtl:peer-placeholder-shown:left-4'
    }
  `.trim();

    const leadingClasses = `
    pointer-events-none absolute flex h-full items-center justify-center px-4 
    ${
      Boolean(label)
        ? 'opacity-0 peer-autofill:opacity-100 peer-focus:opacity-100 peer-[:not(:placeholder-shown)]:opacity-100'
        : ''
    } 
    motion-safe:transition-opacity
    ${noLocale ? 'left-0 rtl:flex-row-reverse' : 'ltr:left-0 rtl:right-0'}
  `.trim();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;

      if (otherProps.type === 'number' || otherProps.type === 'text') {
        const num = String(val);
        if (maxLength && num.length > maxLength) {
          return;
        }
      }
      onChange?.(e);
    };

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setTouched(true);
        otherProps.onBlur?.(e);
      },
      [otherProps.onBlur],
    );

    useEffect(() => {
      if (
        typeof error === 'object' &&
        'type' in error &&
        error?.type === 'manual'
      ) {
        setTouched(true);
      }
    }, [error]);

    return (
      <div className="flex w-full flex-col">
        <div className={containerClasses}>
          <input
            ref={inputRef}
            id={name}
            name={name}
            value={value}
            max={maxLength}
            placeholder={otherProps.placeholder || ' '}
            maxLength={maxLength}
            onChange={handleChange}
            className={cx(inputClasses, inputClassName)}
            lang={otherProps.type === 'number' ? 'en' : undefined}
            {...otherProps}
            onBlur={handleBlur}
          />
          <div className="absolute inset-0 z-50 hidden w-full rounded-xl opacity-0 peer-disabled:block peer-disabled:bg-surface-container-highest peer-disabled:opacity-50" />
          {leading && (
            <div className={leadingClasses}>
              {typeof leading === 'string' ? (
                <p className="text-xs font-medium text-on-surface-medium">
                  {leading}
                </p>
              ) : (
                leading
              )}
            </div>
          )}
          <label htmlFor={name} className={labelClasses}>
            <p>{label}</p>
          </label>
          {trailing && (
            <div
              className={`absolute flex h-full items-center justify-center px-4 ${
                noLocale ? 'right-0' : 'ltr:right-0 rtl:left-0'
              }`}
            >
              <p className="text-xs font-medium text-on-surface-medium">
                {trailing}
              </p>
            </div>
          )}
        </div>
        {touched && messages && (
          <ul className="px-4 pt-1 text-xs text-danger">
            {(Array.isArray(messages) ? messages : [messages]).map(
              (message, index) => (
                <li key={index}>{message}</li>
              ),
            )}
          </ul>
        )}
      </div>
    );
  },
);

export default TextField;
